import {
  type DefaultError,
  type MutationOptions,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'

/**
 * Padrão de mutation com optimistic update aplicado a UMA lista cacheada.
 *
 * Por que esse helper existe:
 * - Quase toda mutation que altera uma coleção (criar / atualizar / remover
 *   item) repete a sequência: cancelQueries → snapshot → setQueryData → em
 *   erro restaurar snapshot → onSettled invalidate.
 * - Quando esse padrão é copiado em cada arquivo, é fácil esquecer um passo
 *   (típico: faltar o `cancelQueries`, e o refetch concorrente sobrescrever
 *   o estado otimista; ou faltar o rollback). O helper centraliza para que
 *   `useCreateAgent`, `useCreateTask`, `useUpdateTask`, `useApproveApproval`
 *   etc. tenham comportamento idêntico e provado em testes.
 *
 * Como usar:
 *   const opts = createOptimisticListMutation<Task, CreateTaskInput, Task[]>({
 *     queryClient,
 *     queryKey: queryKeys.tasks.list(companyId),
 *     mutationFn: (input) => createTask(companyId, input),
 *     applyOptimistic: (old = [], input) => [...old, makeOptimisticTask(input)],
 *   })
 *   useMutation(opts)
 */

export interface OptimisticListMutationConfig<
  TList,
  TInput,
  TResult = unknown,
> {
  queryClient: QueryClient
  queryKey: QueryKey

  /** Chamada de rede que efetiva a mutation. */
  mutationFn: (input: TInput) => Promise<TResult>

  /**
   * Aplica o patch otimista. Recebe a lista atual (pode ser `undefined` se a
   * query nunca foi carregada) e o input da mutation. Retorne a lista nova ou
   * `undefined` para pular o patch (raro — preferir patch idempotente).
   */
  applyOptimistic: (current: TList | undefined, input: TInput) => TList | undefined

  /**
   * Outras chaves a invalidar em `onSettled`. Útil quando uma mutation tem
   * efeito colateral em outra entidade (ex.: `kill agent` invalida tasks
   * porque `assignedToAgentId` pode ficar órfão na visualização).
   */
  invalidateAlso?: QueryKey[]

  /** Hook adicional após sucesso (ex.: toast). NUNCA usar para invalidate — é feito automático. */
  onSuccess?: (result: TResult, input: TInput) => void

  /** Hook adicional após erro (ex.: toast de falha). Rollback já foi aplicado. */
  onError?: (error: DefaultError, input: TInput) => void
}

interface MutationContext<TList> {
  previous: TList | undefined
}

export function createOptimisticListMutation<TList, TInput, TResult = unknown>(
  config: OptimisticListMutationConfig<TList, TInput, TResult>,
): MutationOptions<TResult, DefaultError, TInput, MutationContext<TList>> {
  const { queryClient, queryKey, mutationFn, applyOptimistic } = config
  const extraKeys = config.invalidateAlso ?? []

  return {
    mutationFn,

    onMutate: async (input) => {
      // Pausa qualquer refetch em voo para que ele não sobrescreva o patch.
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<TList>(queryKey)

      const next = applyOptimistic(previous, input)
      if (next !== undefined) {
        queryClient.setQueryData<TList>(queryKey, next)
      }

      return { previous }
    },

    onError: (error, input, ctx) => {
      // Rollback: restaura snapshot capturado em onMutate.
      if (ctx) {
        queryClient.setQueryData<TList | undefined>(queryKey, ctx.previous)
      }
      config.onError?.(error, input)
    },

    onSuccess: (result, input) => {
      config.onSuccess?.(result, input)
    },

    onSettled: () => {
      // Reconcilia estado real (substitui o patch otimista pelo retorno do
      // servidor). Disparado tanto em sucesso quanto em erro pós-rollback.
      void queryClient.invalidateQueries({ queryKey })
      for (const key of extraKeys) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  }
}
